<template>
    <div>
        <simple-modal :show="showContactForm" @close="toggleContactForm" max-width="sm:max-w-2xl" body-class="px-2">
            <div slot="title" class="p-6 flex items-center justify-between">
                <div class="flex items-center gap-x-2">
                    <h4 class="modal-title font-normal">{{ modalTitle }}</h4>

                    <badge class="bg-indigo-100 text-indigo-600" v-if="form.designation">
                        {{ form.designation | titlecase }}
                    </badge>

                    <badge class="bg-yellow-100 text-yellow-600"
                           v-if="mutableContact.archived_at">
                        Archived
                    </badge>
                </div>

                <button class="close" @click="closeForm()" aria-hidden="true">×</button>
            </div>

            <div class="full-height-scroll">
                <form class="form" ref="contactEditForm">
                    <!--VIEW CONTACT WHEN NOT EDITING-->
                    <transition name="fade" mode="out-in">
                        <div v-if="!editing">
                            <div class="col-md-12 col-lg-12">
                                <h4 class="text-lg font-normal mb-4 mt-4 flex items-center gap-2">
                                    Contact Details
                                    <model-replication-tooltip v-if="mutableContact.replication_details" :replication-details="mutableContact.replication_details">
                                    </model-replication-tooltip>
                                </h4>
                                <div class="row mb-12">
                                    <div class="col-sm-12">
                                        <div v-show="contact.contact_name" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium"
                                                      style="min-width: 100px">Contact Name</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.contact_name }}</span>
                                            </div>
                                        </div>

                                        <div v-show="contact.email" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium" style="min-width: 100px">Email</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.email }}</span>
                                            </div>
                                        </div>

                                        <div v-show="contact.email_other" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium" style="min-width: 100px">
                                                    CC Emails
                                                </span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.email_other }}</span>
                                            </div>
                                        </div>

                                        <div v-show="contact.mobile" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium" style="min-width: 100px">Mobile</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.mobile }}</span>
                                            </div>
                                        </div>

                                        <div v-show="contact.phone" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium" style="min-width: 100px">Phone</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.phone }}</span>
                                            </div>
                                        </div>

                                        <div v-show="contact.skype" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium" style="min-width: 100px">Skype</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.skype }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <h4 class="text-lg font-normal mt-4 mb-4" v-show="contact.represents_juristic_entity">
                                    Representing <strong>{{ contact.juristic_name }}</strong></h4>
                                <div class="row">
                                    <div class="col-12">
                                        <div v-show="contact.registration_number" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium"
                                                      style="display:inline-block">Reg Nr</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.registration_number }}</span>
                                            </div>
                                        </div>

                                        <div v-show="contact.vat_number" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium"
                                                      style="display:inline-block">VAT Nr</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.vat_number }}</span>
                                            </div>
                                        </div>

                                        <div v-show="contact.website" class="row mb-1">
                                            <div class="col-sm-4 col-md-3">
                                                <span class="font-medium"
                                                      style="display:inline-block">Website</span>
                                            </div>
                                            <div class="col-sm-8 col-md-9">
                                                <span>{{ contact.website }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>


                        <!--EDIT CONTACT SECTION-->
                        <div v-if="editing" class="px-4">
                            <!--Title,  First Names, Last Name-->
                            <div class="form-group row">
                                <div class="col-md-2">
                                    <label>Title</label>
                                    <el-select
                                        id="title"
                                        v-model="form.title"
                                        style="width: 100%">
                                        <el-option key="none"
                                                   label="None"
                                                   value="">
                                        </el-option>
                                        <el-option
                                            v-for="contactTitle in titleOptions"
                                            :key="contactTitle"
                                            :label="contactTitle"
                                            :value="contactTitle">
                                        </el-option>
                                    </el-select>
                                </div>

                                <div class="col-md-6">
                                    <label>First Names</label>
                                    <input v-model="form.first_names"
                                           @blur="setContactName"
                                           class="form-control"
                                           type="text"
                                           id="first_names"
                                           name="first_names"
                                           :class="{'is-invalid': form.errors.has('first_names')}">

                                    <span class="invalid-feedback" v-show="form.errors.has('first_names')">
                                        {{ form.errors.get("first_names") }}
                                    </span>
                                </div>

                                <div class="col-md-4">
                                    <label>Last Name</label>
                                    <input v-model="form.last_name"
                                           class="form-control"
                                           type="text"
                                           id="last_name"
                                           name="last_name"
                                           :class="{'is-invalid': form.errors.has('last_name')}">

                                    <span class="invalid-feedback" v-show="form.errors.has('last_name')">
                                        {{ form.errors.get("last_name") }}
                                    </span>
                                </div>
                            </div>

                            <!--Contact Name & Type-->
                            <div class="form-group row">
                                <div class="col-md-8">
                                    <label>Contact Name</label>
                                    <input v-model="form.contact_name"
                                           class="form-control"
                                           value="form.contact_name"
                                           type="text"
                                           id="contact_name"
                                           name="contact_name"
                                           :class="{'is-invalid': form.errors.has('contact_name')}">

                                    <span class="invalid-feedback" v-show="form.errors.has('contact_name')">
                                        {{ form.errors.get("contact_name") }}
                                    </span>
                                </div>


                                <div class="col-md-4">
                                    <label>Designation</label>
                                    <input v-model.trim="form.designation"
                                           placeholder="eg. Accountant"
                                           class="form-control"
                                           id="designation"
                                           @blur="formatDesignation"
                                           name="designation"
                                           :class="{'is-invalid': form.errors.has('designation')}">

                                    <span class="invalid-feedback"
                                          v-show="form.errors.has('designation')">{{ form.errors.get("designation") }}
                                    </span>
                                </div>
                            </div>

                            <!--Email, Always CC Other-->
                            <div class="form-group row">
                                <div class="col-md-8">
                                    <label>Email</label>
                                    <input v-model="form.email"
                                           class="form-control"
                                           type="email"
                                           id="email"
                                           name="email"
                                           :class="{'is-invalid': form.errors.has('email')}">

                                    <span class="invalid-feedback"
                                          v-show="form.errors.has('email')">{{ form.errors.get("email") }}
                                    </span>
                                </div>

                                <div class="col-md-4" style="margin-top: 35px">
                                    <label class="custom-control custom-checkbox md-6">
                                        <input v-model="form.always_cc_other" type="checkbox"
                                               class="custom-control-input">
                                        <span class="custom-control-label">Always CC other emails</span>
                                        <i class="mdi mdi-help-circle" data-toggle="tooltip"
                                           title="When selected, any email listed below will always be CC'd."></i>
                                    </label>
                                </div>
                            </div>

                            <!--Other Emails-->
                            <div class="form-group row">
                                <div class="col-md-12">
                                    <label>Other Emails <span
                                        class="small">(Separate with commas)</span></label>
                                    <input v-model="form.email_other"
                                           class="form-control"
                                           type="text"
                                           id="email_other"
                                           name="email_other"
                                           :class="{'is-invalid': form.errors.has('email_other')}">

                                    <span class="invalid-feedback" v-show="form.errors.has('email_other')">
                                        {{ form.errors.get("email_other") }}
                                    </span>
                                </div>
                            </div>

                            <!--Mobile, Phone & Skype-->
                            <div class="form-group row">
                                <div class="col-md-4">
                                    <label>Mobile</label>
                                    <input v-model="form.mobile"
                                           class="form-control"
                                           type="text"
                                           id="mobile"
                                           name="mobile"
                                           :class="{'is-invalid': form.errors.has('mobile')}">

                                    <span class="invalid-feedback" v-show="form.errors.has('mobile')">
                                        {{ form.errors.get("mobile") }}
                                    </span>

                                </div>
                                <div class="col-md-4">
                                    <label>Phone</label>
                                    <input v-model="form.phone"
                                           class="form-control"
                                           type="text"
                                           id="phone"
                                           name="phone"
                                           :class="{'is-invalid': form.errors.has('phone')}">

                                    <span class="invalid-feedback" v-show="form.errors.has('phone')">
                                        {{ form.errors.get("phone") }}
                                    </span>

                                </div>
                                <div class="col-md-4">
                                    <label>Skype</label>
                                    <input v-model="form.skype"
                                           class="form-control"
                                           type="text"
                                           id="Skype"
                                           name="Skype"
                                           :class="{'is-invalid': form.errors.has('skype')}">

                                    <span class="invalid-feedback" v-show="form.errors.has('skype')">
                                        {{ form.errors.get("skype") }}
                                    </span>
                                </div>
                            </div>

                            <!--Represents Juristic Entity checkbox-->
                            <div class="form-group row" v-if="!hideJuristicSection">
                                <div class="col-md-12" style="margin-top:20px">
                                    <label class="custom-control custom-checkbox md-6">
                                        <input v-model="form.represents_juristic_entity"
                                               type="checkbox"
                                               class="custom-control-input"
                                               name="represents_juristic_entity">
                                        <span v-show="!form.represents_juristic_entity || !form.juristic_type"
                                              class="custom-control-label">Represents Juristic Entity</span>
                                        <span v-show="form.represents_juristic_entity && form.juristic_type"
                                              class="custom-control-label">Represents a <strong>{{ form.juristic_type }}</strong></span>
                                        <i class="mdi mdi-help-circle" data-toggle="tooltip"
                                           title="If the contact is representing a juristic entity, select this option to capture the entity details."></i>
                                    </label>
                                </div>
                            </div>

                            <!--Juristic Entity Section-->
                            <div v-show="form.represents_juristic_entity">
                                <!--Juristic Entity Name & Type-->
                                <div class="form-group row" id="juristic-entity-details">
                                    <div class="col-md-8">
                                        <label>Entity Name</label>
                                        <input v-model="form.juristic_name"
                                               class="form-control"
                                               type="text"
                                               id="juristic_name"
                                               name="juristic_name"
                                               :class="{'is-invalid': form.errors.has('juristic_name')}">

                                        <span class="invalid-feedback"
                                              v-show="form.errors.has('juristic_name')">
                                            {{ form.errors.get("juristic_name") }}
                                        </span>
                                    </div>

                                    <div class="col-md-4">
                                        <label>Entity Type</label>
                                        <el-select
                                            id="juristic_type"
                                            v-model="form.juristic_type"
                                            style="width: 100%">
                                            <el-option key="none"
                                                       label="None"
                                                       value="">
                                            </el-option>
                                            <el-option
                                                v-for="entityType in juristicEntityTypes"
                                                :key="entityType"
                                                :label="entityType"
                                                :value="entityType">
                                            </el-option>
                                        </el-select>
                                    </div>
                                </div>

                                <!--Registration & VAT Nr-->
                                <div class="form-group row">
                                    <div class="col-md-4">
                                        <label>Registration Nr.</label>
                                        <input v-model="form.registration_number"
                                               class="form-control"
                                               type="text"
                                               id="registration_number"
                                               name="registration_number"
                                               :class="{'is-invalid': form.errors.has('registration_number')}">

                                        <span class="invalid-feedback"
                                              v-show="form.errors.has('registration_number')">
                                            {{ form.errors.get("registration_number") }}
                                        </span>
                                    </div>
                                    <div class="col-md-4">
                                        <label>Vat Nr.</label>
                                        <input v-model="form.vat_number"
                                               class="form-control"
                                               type="text"
                                               id="vat_number"
                                               name="vat_number"
                                               :class="{'is-invalid': form.errors.has('vat_number')}">

                                        <span class="invalid-feedback" v-show="form.errors.has('vat_number')">
                                            {{ form.errors.get("vat_number") }}
                                        </span>
                                    </div>
                                </div>

                                <!--Website-->
                                <div class="form-group row mb-4">
                                    <div class="col-md-8">
                                        <label>Website</label>
                                        <input v-model="form.website"
                                               placeholder="eg. https://mywebsite.com"
                                               class="form-control"
                                               type="url"
                                               id="website"
                                               name="website"
                                               :class="{'is-invalid': form.errors.has('website')}">

                                        <span class="invalid-feedback" v-show="form.errors.has('website')">
                                            {{ form.errors.get("website") }}
                                        </span>
                                    </div>
                                </div>

                            </div>
                            <!--End Juristic Section-->

                            <!--Prevent External Updates checkbox-->
                            <div class="form-group row">
                                <div class="col-md-12">
                                    <label class="custom-control custom-checkbox md-6">
                                        <input v-model="form.prevent_external_updates"
                                               type="checkbox"
                                               class="custom-control-input"
                                               name="prevent_external_updates">
                                        <span class="custom-control-label">Prevent external data integrations from updating this record.</span>
                                        <i class="mdi mdi-help-circle" data-toggle="tooltip"
                                           title="Prevent data syncing from RAMS from overriding changes you make to this record."></i>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </transition>
                </form>
            </div>

            <div class="p-4 flex items-center space-x-2">
                <div v-if="editing" class="flex justify-end items-center space-x-2 w-full">
                    <button class="btn waves-effect waves-light hover:bg-gray-200" :disabled="form.busy" @click.prevent="cancelEdit">
                        Cancel
                    </button>

                    <secondary-button name="Save & Back" v-if="userCan('manage-contacts')" @click="updateContact"></secondary-button>
                </div>

                <div v-if="!editing" class="flex justify-between items-center gap-2 w-full">
                    <div class="flex items-center gap-2">
                        <button class="btn waves-effect waves-light hover:bg-gray-200"
                                @click.prevent="archiveOrUnarchiveModel(!mutableContact.archived_at)">
                            {{ mutableContact.archived_at ? "Unarchive" : "Archive" }}
                        </button>

                        <a target="_blank"
                           class="btn waves-effect waves-light hover:bg-gray-200"
                           :href="'/activity-log?id=' + mutableContact.id + '&type=Contact'">
                            Activity Log
                        </a>

                        <button class="btn waves-effect waves-light hover:bg-gray-200" @click="toggleShareModal()">
                            Share Maintenance Link
                        </button>
                    </div>

                    <secondary-button name="Edit" v-if="userCan('manage-contacts')" @click="showUpdateFields"></secondary-button>
                </div>
            </div>
        </simple-modal>

        <archive-and-unarchive-modal :show="showArchivingModal" :model-id="mutableContact.id" model-type="contact" :archiving="isArchiving"
                                     @actioned="afterArchiving" v-if="showArchivingModal" @close="toggleArchivingModal">
        </archive-and-unarchive-modal>

        <share-maintenance-request-link-modal :show="showShareModal" v-if="showShareModal" :tenant="mutableContact"
                                              @toggle-modal="toggleShareModal">
        </share-maintenance-request-link-modal>
    </div>
</template>

<script>
import ContactFieldValidations from "../../mixins/contact-mixin";
import ContactsMixin from "../../mixins/contacts-mixin";
import ContactFormListsMixin from "../../mixins/contact-form-lists-mixin";
import ArchivingMixin from "../../mixins/archiving-mixin";

export default {
    name: "contact-view-edit",

    mixins: [ArchivingMixin, ContactFieldValidations, ContactsMixin, ContactFormListsMixin],

    props: ["contact", "contactable", "hideJuristicSection", "role"],

    data () {
        return {
            editing: false,
            form: new AppForm({}),
            archiveForm: new AppForm({}),
            mutableContact: this.contact,
            showShareModal: false,
        };
    },

    computed: {
        modalTitle () {
            if (!this.form.first_names && !this.form.last_name && this.contact.full_name) {
                return this.contact.full_name;
            }
            let name = [
                this.form.title ? this.form.title + ". " : "",
                this.form.first_names,
                this.form.last_name
            ].filter(name => name).join(" ");

            return name ?? "Contact Details";
        },
    },

    watch: {
        contact: {
            handler: function (newVal) {
                this.$set(this, "mutableContact", newVal);
            },
            deep: true,
        },

        form: function (newVal) {
            this.form = newVal;
        },
    },

    methods: {
        showUpdateFields () {
            this.form = new AppForm(this.mutableContact);
            this.editing = true;
        },

        formatEmail (email) {
            return email.toLowerCase().trim();
        },

        closeForm () {
            this.cancelEdit();
            this.toggleContactForm();
        },

        cancelEdit () {
            this.form = new AppForm(this.mutableContact);
            this.editing = false;
        },

        updateContact () {
            if (!this.form.represents_juristic_entity) {
                this.form.juristic_name = null;
                this.form.juristic_type = null;
                this.form.registration_number = null;
                this.form.vat_number = null;
                this.form.website = null;
            }

            App.patch("/contacts/" + this.form.id + "/update", this.form)
            .then((updatedContact) => {
                this.$parent.currentContact = updatedContact;
                window.toastr.success("Details successfully updated");
                Bus.$emit("contact-updated", this.selectedRole);
                this.$emit("contact-updated", updatedContact);
                this.editing = false;
            }).catch(() => {
                window.toastr.error("Could not update contact!");
            });
        },

        formatDesignation () {
            this.form.designation = this.$options.filters.titlecase(this.form.designation);
        },

        afterArchiving (archiving) {
            if (archiving) {
                this.mutableContact.archived_at = this.$dayjs();
            } else {
                this.mutableContact.archived_at = null;
            }
            // Refresh contacts list in background
            Bus.$emit("contact-updated");
        },

        toggleShareModal () {
            this.showShareModal = !this.showShareModal;
        },
    },
};
</script>
